import type { AssetFields } from 'contentful'
import type { ImgHTMLAttributes } from 'react'
import type { ContentfulImageOptions } from '~/libs/contentful/helpers'
import { removeUndefinedFromObjcet } from '~/utils/clearQuery'

type ResponsiveImageProps = {
  asset: AssetFields
  opts?: {
    alt?: string
    load?: 'lazy'
  } & Omit<ContentfulImageOptions, 'quality'> &
    Partial<Pick<ContentfulImageOptions, 'quality'>>
} & ImgHTMLAttributes<HTMLImageElement>

type AcceptableImageFormatType = 'webp' | 'avif' | 'png' | 'jpg'
const MOST_USED_SIZES = [639, 767, 1023, 1365, 1599, 1919] as const

const imgStyle = {
  width: 'inherit',
  minHeight: '100%',
  minWidth: '100%',
  maxHeight: 'inherit',
} as const

// SIZES: Source - https://medium.com/hceverything/applying-srcset-choosing-the-right-sizes-for-responsive-images-at-different-breakpoints-a0433450a4a3
// 1920px (this covers FullHD screens and up)
// 1600px (this will cover 1600px desktops and several tablets in portrait mode, for example iPads at 768px width, which will request a 2x image of 1536px and above)
// 1366px (it is the most widespread desktop resolution)
// 1024px (1024x768 screens, excluding iPads which are hi-density anyway, are rarer, but I think you need some image size in between, not to leave too big a gap between pixel sizes, in case the market changes)
// 768px (useful for 2x 375px mobile screens, as well as any device that actually requests something close to 768px)
// 640px (for smartphones)

export const ResponsiveImage = ({
  asset,
  opts,
  className,
  style,
  fetchPriority,
}: ResponsiveImageProps): JSX.Element => {
  const { file, title } = asset || { file: undefined, title: '' }
  const { alt, quality, fit, width, height, focus, radius, load } = opts || {
    quality: 80,
  }

  if (!file) {
    return <></>
  }
  const transformImageSourceSet = (type: AcceptableImageFormatType) => {
    if (width || height) return transformSingleUrl(file.url, type)

    const formattedUrls = MOST_USED_SIZES.map(
      (size) => `${transformSingleUrl(file.url, type, size)} ${size}w`,
    )
    return formattedUrls.join(', ')
  }

  const setBreakpointSizes = () => {
    if (width) return `${width}px`

    const formattedSizes = MOST_USED_SIZES.map((size) => `(max-width: ${size}px) ${size}px`)
    return formattedSizes.join(', ')
  }

  const transformSingleUrl = (url: string, type: AcceptableImageFormatType, size?: number) => {
    const queryObj = {
      fm: type,
      q: quality,
      w: size || width,
      h: height,
      fit: fit,
      f: focus,
      r: radius,
    }

    const query = removeUndefinedFromObjcet(queryObj)
    return `${url}?${new URLSearchParams(query as Record<string, string>).toString()}`
  }

  return (
    <picture>
      <source
        srcSet={transformImageSourceSet('webp')}
        type="image/webp"
        sizes={setBreakpointSizes()}
      />
      <source
        srcSet={transformImageSourceSet('avif')}
        type="image/avif"
        sizes={setBreakpointSizes()}
      />
      <img
        src={`${file.url}?q=${quality || 80}`}
        alt={alt || title}
        sizes={setBreakpointSizes()}
        loading={load || 'eager'}
        className={className}
        style={{ ...imgStyle, ...style }}
        fetchPriority={fetchPriority || 'auto'}
      />
    </picture>
  )
}
